// shell/components/table/columns/badge-column.ts

import { BadgeColumnType } from '../types';
import { TextColumnRenderer } from './text-column';
import { CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class BadgeColumnRenderer<T = unknown> extends TextColumnRenderer<T> {
    constructor(private badgeConfig: BadgeColumnType<T>) {
        super({ kind: 'text', converter: badgeConfig.converter });
    }

    getGridCellClass(): string {
        return 'cs-column-badge';
    }

    getTableCellClass(): string {
        return 'cs-column-badge';
    }

    // Badges never use heat map shading (they have their own colors)
    protected shouldShadeInGrid(): boolean {
        return false;
    }

    protected shouldShadeInTable(): boolean {
        return false;
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement {
        return this.buildPill(ctx);
    }

    buildTableCell(ctx: CellRenderContext<T>, cell: HTMLElement): HTMLElement {
        return this.buildPill(ctx);
    }

    private buildPill(ctx: CellRenderContext<T>): HTMLElement {
        const pill = document.createElement('span');
        pill.className = 'cs-column-badge__pill';

        const text = this.formatValue(ctx.value, this.badgeConfig.converter, ctx.item);
        pill.textContent = text;

        try {
            const bgColor = this.badgeConfig.backgroundColor(ctx.value, ctx.item);
            if (bgColor) {
                pill.style.backgroundColor = bgColor;
            }
        } catch (e) {
            console.error('Badge backgroundColor callback error:', e);
        }

        this.applyTooltip(pill, ctx);

        return pill;
    }

    private applyTooltip(pill: HTMLElement, ctx: CellRenderContext<T>): void {
        if (!this.badgeConfig.tooltip) return;
        try {
            const tip = this.badgeConfig.tooltip(ctx.value, ctx.item);
            if (tip) {
                pill.title = tip;
            } else {
                pill.removeAttribute('title');
            }
        } catch (e) {
            console.error('Badge tooltip callback error:', e);
        }
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        if (type === CellUpdateType.Full && contentEl) {
            const text = this.formatValue(ctx.value, this.badgeConfig.converter, ctx.item);
            contentEl.textContent = text;

            try {
                const bgColor = this.badgeConfig.backgroundColor(ctx.value, ctx.item);
                contentEl.style.backgroundColor = bgColor || '';
            } catch (e) {
                console.error('Badge backgroundColor callback error:', e);
            }

            this.applyTooltip(contentEl, ctx);
        }
        return { contentElement: contentEl };
    }

    updateTableCell(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        return this.updateGridCell(ctx, cell, contentEl, type);
    }
}
